<template>
  <div style="margin-top: 20px">
    <FiPageRail title="Fleet Map" nowrapActions>
      <template #actions>
        <b-button size="sm" variant="outline-secondary" @click="load_vehicles" :disabled="vehicles_loading"
                  title="Reload positions" aria-label="Reload positions">
          <b-spinner v-if="vehicles_loading" small></b-spinner>
          <i v-else class="fa fa-refresh"></i>
        </b-button>
        <b-button size="sm" variant="outline-secondary" @click="showInfo = true" title="About this page" aria-label="About this page">
          <i class="fa fa-info-circle"></i>
        </b-button>
      </template>
    </FiPageRail>

    <b-modal v-model="showInfo" title="About the Fleet Map" ok-only ok-title="Close">
      <p>Current telematics positions for every active tenant on the platform, on one map.</p>
      <ul>
        <li>Filter in the left panel — <strong>click a row</strong> to show only it, or its <strong>checkbox</strong> to toggle just that row. The map refits to whatever is left after each change.</li>
        <li><strong>Click a section heading</strong> to turn the whole group on or off.</li>
        <li>A vehicle shows when its tenant type, telematics provider, and tenant are all checked. Each list and its counts reflect the other filters, so narrowing one narrows the rest.</li>
        <li><em>Show only</em>: <em>Bus SOS enabled customers</em> keeps just the tenants that can receive Bus SOS requests (Bus SOS enabled and opted in to receive from other companies; those carry an <strong>SOS</strong> tag in the list) and, for testing, <strong>labels each bus with its number on the map</strong>, switches <em>Color by</em> to <em>Tenant</em>, and turns <em>Cluster</em> off so every bus shows; <em>Yards instead of vehicles</em> swaps the squares for one ringed marker per tenant yard (from that tenant's settings), labelled with how many vehicles are parked within half a mile of it.</li>
        <li>Markers are plain squares, colored by telematics provider, booking source, or <em>Tenant</em> (a distinct color per company, so two Samsara fleets never share a hue) (<em>Color by</em>); the Bus SOS view deals those companies the furthest-apart colors in the palette. <em>Cluster</em> collapses vehicles into count bubbles in the dominant color: <em>Yards</em> makes one bubble per tenant yard (vehicles within half a mile of a yard in that tenant's settings), <em>Dense</em> merges any crowded spot on screen and splits apart as you zoom in, <em>Off</em> draws every square. Outside the Bus SOS testing view, nothing on the map identifies a vehicle or company; the left panel is the only place tenants are named.</li>
        <li><strong>On map</strong> lists what is currently drawn: each vehicle whose number the page was given (Bus SOS customers only — everyone else's stays out of the browser) beside its company, and one row per remaining company with its count.</li>
        <li>Positions older than 20 days and retired/placeholder units are left out, as on each tenant's own fleet map. Demo fleets are never shown.</li>
        <li>Sister tenants that share a telematics account and tag filter are counted once, under the tenant with the shorter name.</li>
        <li>In production the positions come from a snapshot refreshed every 2 hours; the reload button re-reads the latest snapshot.</li>
      </ul>
    </b-modal>

    <div class="overlay-wrap">
      <div class="overlay-panel">
        <div class="overlay-panel-title">Map filters</div>

        <div class="overlay-group">
          <div class="overlay-group-title">Color by</div>
          <div class="overlay-colorby" role="radiogroup" aria-label="Color squares by">
            <button v-for="opt in COLOR_MODES" :key="opt.value" type="button"
                    class="overlay-colorby-btn" :class="{ 'overlay-colorby-btn--on': color_by === opt.value }"
                    role="radio" :aria-checked="color_by === opt.value" @click="color_by = opt.value">
              {{ opt.label }}
            </button>
          </div>
        </div>

        <div class="overlay-group">
          <div class="overlay-group-title">Cluster</div>
          <div class="overlay-colorby" role="radiogroup" aria-label="Cluster vehicles">
            <button v-for="opt in CLUSTER_MODES" :key="opt.value" type="button"
                    class="overlay-colorby-btn" :class="{ 'overlay-colorby-btn--on': cluster_mode === opt.value }"
                    role="radio" :aria-checked="cluster_mode === opt.value" :title="opt.title" @click="cluster_mode = opt.value">
              {{ opt.label }}
            </button>
          </div>
        </div>

        <div class="overlay-group">
          <div class="overlay-range-row">
            <span class="overlay-group-title overlay-group-title--inline">Marker size</span>
            <span class="overlay-count">{{ marker_size }} px</span>
          </div>
          <input type="range" min="4" max="20" step="1" v-model.number="marker_size"
                 class="overlay-range-slider" aria-label="Marker size in pixels"/>
        </div>

        <div class="overlay-group overlay-range">
          <div class="overlay-group-title">Range filter</div>
          <div v-if="!range_pin" class="overlay-hint overlay-hint--tight">
            Click the map to drop a pin &amp; filter
          </div>
          <template v-else>
            <div class="overlay-range-row">
              <span class="overlay-range-label">Within <strong>{{ range_radius }} mi</strong></span>
              <button type="button" class="overlay-range-clear" @click="clear_range_pin"
                      title="Clear the range pin" aria-label="Clear the range pin">
                <i class="fa fa-times"></i>
              </button>
            </div>
            <input type="range" min="5" max="150" step="5" v-model.number="range_radius"
                   class="overlay-range-slider" aria-label="Range radius in miles"/>
          </template>
        </div>

        <div class="overlay-group">
          <div class="overlay-group-title">Show only</div>
          <div class="overlay-layer" title="Show only vehicles of Bus SOS enabled customers" @click="sos_only = !sos_only">
            <input type="checkbox" v-model="sos_only" title="Bus SOS enabled customers only" @click.stop/>
            <span class="overlay-layer-label">Bus SOS enabled customers</span>
            <span class="overlay-count">{{ sosTenantIds.size }}</span>
          </div>
          <div class="overlay-layer" title="Show the tenants' yards instead of vehicles, each with its parked count" @click="yards_only = !yards_only">
            <input type="checkbox" v-model="yards_only" title="Yards instead of vehicles" @click.stop/>
            <span class="overlay-layer-label">Yards instead of vehicles</span>
            <span class="overlay-count">{{ yardPoints.length }}</span>
          </div>
        </div>

        <div class="overlay-group">
          <button type="button" class="overlay-group-title overlay-group-title--btn"
                  title="Toggle this section" @click="toggle_section('type')">Tenant types</button>
          <div v-for="row in tenantTypeList" :key="'type:' + row.key" class="overlay-layer"
               title="Show only this tenant type" @click="isolate('type', row.key)">
            <input type="checkbox" :checked="row.on" title="Toggle this tenant type"
                   @click.stop @change="toggle_row('type', row.key, row.on)"/>
            <span v-if="color_by === 'type'" class="overlay-swatch" :style="{ background: typeColor(row.key) }"></span>
            <span class="overlay-layer-label">{{ row.label }}</span>
            <span class="overlay-count">{{ row.count }}</span>
          </div>
          <div v-if="!vehicles_loading && !tenantTypeList.length" class="overlay-hint overlay-hint--tight">
            <template v-if="source === 'none'">No snapshot yet. The refresh job runs every 2 hours in production.</template>
            <template v-else>No live vehicle positions reported.</template>
          </div>
        </div>

        <div class="overlay-group">
          <button type="button" class="overlay-group-title overlay-group-title--btn"
                  title="Toggle this section" @click="toggle_section('telematics')">Telematics types</button>
          <div v-for="row in telematicsList" :key="'tel:' + row.key" class="overlay-layer"
               title="Show only this telematics type" @click="isolate('telematics', row.key)">
            <input type="checkbox" :checked="row.on" title="Toggle this telematics type"
                   @click.stop @change="toggle_row('telematics', row.key, row.on)"/>
            <span v-if="color_by === 'telematics'" class="overlay-swatch" :style="{ background: telematicsColor(row.key) }"></span>
            <span class="overlay-layer-label">{{ row.label }}</span>
            <span class="overlay-count">{{ row.count }}</span>
          </div>
        </div>

        <div class="overlay-group">
          <div class="overlay-group-head">
            <button type="button" class="overlay-group-title overlay-group-title--btn"
                    title="Toggle this section" @click="toggle_section('tenant')">Tenants</button>
            <button type="button" class="overlay-mini-btn" title="Select every tenant"
                    :disabled="tenantList.every(r => r.on)" @click="set_section('tenant', true)">All</button>
            <button type="button" class="overlay-mini-btn" title="Deselect every tenant"
                    :disabled="!tenantList.some(r => r.on)" @click="set_section('tenant', false)">None</button>
          </div>
          <div class="overlay-network-list">
            <div v-for="row in tenantList" :key="'tenant:' + row.key" class="overlay-layer"
                 :title="'Show only ' + row.label" @click="isolate('tenant', row.key)">
              <input type="checkbox" :checked="row.on" title="Toggle this tenant"
                     @click.stop @change="toggle_row('tenant', row.key, row.on)"/>
              <span class="overlay-swatch" :style="{ background: row.color }"></span>
              <span class="overlay-layer-label">{{ row.label }}</span>
              <span v-if="sosTenantIds.has(row.key)" class="overlay-member-tag overlay-member-tag--sos" title="Bus SOS enabled">SOS</span>
              <span class="overlay-member-tag">{{ row.tenant_type }}</span>
              <span class="overlay-count">{{ row.count }}</span>
            </div>
          </div>
        </div>

        <div v-if="quietTenants.length || sharedTenants.length || erroredTenants.length" class="overlay-hint">
          <template v-if="quietTenants.length">
            {{ quietTenants.length }} {{ quietTenants.length === 1 ? 'tenant has' : 'tenants have' }} telematics but no live positions.
          </template>
          <template v-if="sharedTenants.length">
            <span :title="sharedTenants.map(t => t.tenant_name + ' → ' + t.shared_with).join('\n')">
              {{ sharedTenants.length }} sister {{ sharedTenants.length === 1 ? 'tenant shares' : 'tenants share' }} an account and {{ sharedTenants.length === 1 ? 'is' : 'are' }} counted once.
            </span>
          </template>
          <template v-if="erroredTenants.length">
            <span class="overlay-hint--error" :title="erroredTenants.map(t => t.tenant_name + ': ' + t.error).join('\n')">
              {{ erroredTenants.length }} {{ erroredTenants.length === 1 ? 'tenant' : 'tenants' }} could not be read.
            </span>
          </template>
        </div>

        <div class="overlay-group overlay-shown">
          <div class="overlay-group-title">
            On map
            <span v-if="yards_only" class="overlay-count">{{ yardPoints.length }} of {{ yards.length }} yards</span>
            <span v-else class="overlay-count">{{ points.length }} of {{ vehicles.length }}</span>
          </div>
          <div v-if="onMapList.rows.length" class="overlay-onmap-list">
            <div v-for="row in onMapList.rows" :key="row.key" class="overlay-layer overlay-layer--static"
                 :title="row.tenant ? row.label + ' · ' + row.tenant : row.label">
              <span class="overlay-swatch" :style="{ background: row.color }"></span>
              <span class="overlay-layer-label">{{ row.label }}</span>
              <span v-if="row.tenant" class="overlay-onmap-tenant">{{ row.tenant }}</span>
              <span v-if="row.count" class="overlay-count">{{ row.count }}</span>
            </div>
          </div>
          <div v-if="onMapList.more" class="overlay-hint overlay-hint--tight overlay-hint--nomargin">
            +{{ onMapList.more }} more not listed
          </div>
          <div v-if="generated_at" class="overlay-hint overlay-hint--tight overlay-hint--nomargin">
            Snapshot · {{ as_of(generated_at) }}
          </div>
          <div v-if="vehicles_loading" class="overlay-hint overlay-hint--tight overlay-hint--nomargin">
            Loading positions…
          </div>
        </div>
      </div>

      <div class="overlay-mapwrap">
        <div v-if="loadError" class="overlay-state overlay-state--error">
          Couldn't load fleet positions. Try again or contact support.
        </div>
        <gmap-map
            ref="overlayMap"
            :zoom="CONUS_ZOOM"
            :center="CONUS_CENTER"
            :options="themed_map_options"
            class="overlay-map"
            @click="place_range_pin">
          <gmap-circle v-if="range_pin" :center="range_pin" :radius="rangeMeters"
                       :options="{ strokeColor: '#c0392b', strokeOpacity: 0.6, strokeWeight: 1, fillColor: '#c0392b', fillOpacity: 0.06, clickable: false }"/>
          <gmap-marker v-if="range_pin" :position="range_pin" :icon="rangePinIcon" :draggable="true"
                       :z-index="9999" @dragend="place_range_pin"/>
        </gmap-map>
      </div>
    </div>
  </div>
</template>

<script>
import { get_admin_fleet_map } from '../networking/apis'
import { mapState } from 'vuex'
import { DARK_MAP_STYLES } from '../assets/dark_map_styles'

// geographic center of the lower 48; zoom 4 frames the whole continental US
// on a typical desktop map pane. fixed on purpose: no geolocation, no fitBounds.
const CONUS_CENTER = { lat: 39.5, lng: -98.35 }
const CONUS_ZOOM = 4

// one stable color per telematics provider; anything unexpected draws from the spare palette
const TELEMATICS_COLORS = {
  SAMSARA: '#16a085',
  MOTIVE: '#8e44ad',
  ZONAR: '#e67e22',
  SAUCON: '#23608f',
  VERIZON_FLEETMATICS: '#c0392b',
}
const SPARE_COLORS = ['#b8860b', '#a4508b', '#5b6e2f', '#3f6fb0', '#8a6d3b']
const UNKNOWN_KEY = '(unknown)'

// the per-tenant palette, hand-ordered so the FIRST colors are the ones furthest apart: hue,
// saturation and lightness all move between neighbours, so a handful of tenants (the Bus SOS
// customers, say) never lands on three shades of the same green. every entry is dark enough
// for the white count text drawn on cluster bubbles. past the end of the list the generated
// golden-angle hues take over.
const TENANT_COLORS = [
  '#1f6fd0', // blue
  '#d1500f', // vermillion
  '#1a8a4b', // green
  '#b0269a', // magenta
  '#9a7d0a', // dark gold
  '#6a35c9', // purple
  '#0f8b8d', // teal
  '#c0223f', // crimson
  '#5a8f1e', // olive
  '#d9528a', // rose
  '#14457f', // navy
  '#a63a10', // rust
  '#12876f', // sea green
  '#8e44ad', // violet
  '#b4690e', // amber
  '#3b4cc0', // indigo
  '#4a7a9b', // steel
  '#7b2d5e', // plum
  '#2f6f3e', // forest
  '#a05ec9', // orchid
  '#8a5a2b', // brown
  '#0e7490', // cyan
  '#7aa61b', // lime
  '#566573', // slate
]

// the On map list names what is drawn; past this many rows it stops listing and says how many
// more there are, so a national view never renders thousands of panel rows
const MAX_ON_MAP_ROWS = 400

const COLOR_MODES = [
  { value: 'telematics', label: 'Telematics' },
  { value: 'type', label: 'Bookings' },
  { value: 'tenant', label: 'Tenant' },
]

// hsl -> #rrggbb, so the per-tenant palette can be generated at any size
function hslToHex(h, s, l) {
  s /= 100
  l /= 100
  const k = n => (n + h / 30) % 12
  const a = s * Math.min(l, 1 - l)
  const f = n => {
    const c = l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)))
    return Math.round(255 * c).toString(16).padStart(2, '0')
  }
  return '#' + f(0) + f(8) + f(4)
}
// default marker square edge in css px, drawn on the canvas overlay (the panel slider overrides).
// 6 is the nearest whole pixel to a quarter up from the original 5; fractional sizes blur.
const DEFAULT_SQUARE = 6
// clustering: screen-space grid cells of this many css px; a cell holding at least
// CLUSTER_MIN squares draws as one count bubble instead
const CELL = 40
const CLUSTER_MIN = 3
// yard mode: a yard with at least this many parked vehicles draws as a bubble
const YARD_MIN = 2
// ...and past this zoom the yard opens up into its squares
const YARD_EXPAND_ZOOM = 15

const CLUSTER_MODES = [
  { value: 'off', label: 'Off', title: 'Draw every vehicle as a square' },
  { value: 'yards', label: 'Yards', title: 'One count bubble per tenant yard; road vehicles stay squares' },
  { value: 'dense', label: 'Dense', title: 'Merge any crowded spot on screen into a count bubble' },
]

function formatCount(n) {
  return n >= 1000 ? (n / 1000).toFixed(n >= 10000 ? 0 : 1) + 'k' : String(n)
}

// google's web-mercator "world" coordinate (256px world), computed once per vehicle so
// the overlay's draw is pure arithmetic with no per-point projection calls
function worldPoint(lat, lng) {
  const siny = Math.min(Math.max(Math.sin((lat * Math.PI) / 180), -0.9999), 0.9999)
  return {
    wx: 256 * (0.5 + lng / 360),
    wy: 256 * (0.5 - Math.log((1 + siny) / (1 - siny)) / (4 * Math.PI)),
  }
}

export default {
  name: 'admin_fleet_map',
  data() {
    return {
      CONUS_CENTER,
      CONUS_ZOOM,
      COLOR_MODES,
      CLUSTER_MODES,
      color_by: 'telematics',
      sos_only: false,
      yards_only: false,
      cluster_mode: 'yards',
      marker_size: DEFAULT_SQUARE,
      loadError: false,
      showInfo: false,
      range_pin: null,
      range_radius: 25,
      // frozen on load: thousands of reactive rows cost more than the map itself
      vehicles: [],
      tenants: [],
      yards: [],
      generated_at: null,
      source: null,
      vehicles_loading: false,
      // per-facet on/off overrides; a key that is absent is on
      type_on: {},
      telematics_on: {},
      tenant_on: {},
      // matches the fleet map basemap (vehicle_map.vue); midnight users get the dark variant
      mapStyle: {
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: false,
        styles: [
          { featureType: 'landscape', stylers: [{ color: '#F3F4F9' }] },
          { featureType: 'water', stylers: [{ color: '#C1CDE4' }] },
          { featureType: 'landscape.natural.terrain', stylers: [{ color: '#E0EDCA' }] },
          { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#DDDDDD' }] },
          { featureType: 'road', elementType: 'geometry.fill', stylers: [{ color: '#FFFFFF' }] },
          { featureType: 'road.highway', elementType: 'geometry.fill', stylers: [{ color: '#FEF8D6' }] },
          { featureType: 'transit', stylers: [{ visibility: 'off' }] },
          { featureType: 'poi', stylers: [{ visibility: 'off' }] },
        ],
      },
    }
  },
  computed: {
    ...mapState({
      user: state => state.profile.user,
    }),
    themed_map_options() {
      const midnight = (this.$store.state.profile || {}).site_theme === 'midnight'
      return midnight ? { ...this.mapStyle, styles: DARK_MAP_STYLES } : this.mapStyle
    },
    tenantNames() {
      const names = {}
      this.tenants.forEach(t => { names[t.tenant_id] = t.tenant_name || t.tenant_id })
      return names
    },
    // each facet list reflects the vehicles that pass every OTHER filter (bus sos, range and
    // the two remaining facets), so picking Bus SOS or a tenant type narrows the other lists
    // and their counts; a row you unchecked still shows, so it can be checked again
    tenantTypeList() {
      return this.facet_rows(this.vehicles.filter(v => this.passes(v, 'type')), v => v.type_key, this.type_on)
    },
    telematicsList() {
      return this.facet_rows(this.vehicles.filter(v => this.passes(v, 'telematics')), v => v.telematics_key, this.telematics_on)
    },
    tenantList() {
      // one row per tenant with a position passing the other filters, labelled by tenant name
      const meta = {}
      const rows = this.vehicles.filter(v => this.passes(v, 'tenant'))
      rows.forEach(v => {
        if (!meta[v.tenant_id]) {
          meta[v.tenant_id] = { label: this.tenantNames[v.tenant_id] || v.tenant_id, tenant_type: v.type_key, telematics_type: v.telematics_key }
        }
      })
      return this.facet_rows(rows, v => v.tenant_id, this.tenant_on)
        .map(row => ({ ...row, ...meta[row.key] }))
        .sort((a, b) => a.label.localeCompare(b.label))
        .map(row => ({ ...row, color: this.colorFor({ tenant_id: row.key, type_key: row.tenant_type, telematics_key: row.telematics_type }) }))
    },
    typeColors() {
      // one color per tenant type (CM, TBN, ...), stable in sorted order
      const map = {}
      this.allTypeKeys.forEach((key, i) => { map[key] = SPARE_COLORS[i % SPARE_COLORS.length] })
      return map
    },
    // palettes key off EVERY loaded vehicle, not the cascaded lists, so a color never
    // changes just because a filter narrowed the legend
    allTypeKeys() {
      return [...new Set(this.vehicles.map(v => v.type_key))].sort()
    },
    allTelematicsKeys() {
      return [...new Set(this.vehicles.map(v => v.telematics_key))].sort()
    },
    orderedColors() {
      // stable spare-palette assignment for providers not in TELEMATICS_COLORS
      const map = { ...TELEMATICS_COLORS }
      let i = 0
      this.allTelematicsKeys.forEach(key => {
        if (!map[key]) map[key] = SPARE_COLORS[i++ % SPARE_COLORS.length]
      })
      return map
    },
    colorScopeTenantIds() {
      // the tenants the palette is dealt across: every loaded tenant normally, and only the Bus
      // SOS customers while that view is on. keying off the Show only toggle rather than the
      // facet checkboxes means ticking a row never reshuffles colors, while the four-tenant SOS
      // view still gets the four furthest-apart colors instead of three shades of one green.
      const ids = new Set()
      for (const v of this.vehicles) {
        if (this.sos_only && !this.sosTenantIds.has(v.tenant_id)) continue
        ids.add(v.tenant_id)
      }
      return [...ids].sort()
    },
    tenantColors() {
      // a distinct color per tenant, so same-provider companies (e.g. two Samsara fleets) never
      // share a hue. the curated palette comes first (its head is the most distinct); past its
      // end, golden-angle hue rotation with alternating saturation/lightness keeps going.
      const map = {}
      this.colorScopeTenantIds.forEach((id, i) => {
        if (i < TENANT_COLORS.length) {
          map[id] = TENANT_COLORS[i]
          return
        }
        const n = i - TENANT_COLORS.length
        const hue = (n * 137.508 + 23) % 360
        const sat = 62 + (n % 3) * 9      // 62 / 71 / 80
        const light = 42 + (n % 2) * 8    // 42 / 50
        map[id] = hslToHex(hue, sat, light)
      })
      return map
    },
    quietTenants() {
      return this.tenants.filter(t => t.has_telematics && !t.vehicle_count && !t.error && !t.shared_with)
    },
    sosTenantIds() {
      // customers that can RECEIVE SOS requests: Bus SOS enabled AND opted in to receive from others.
      // demo accounts are excluded so the count matches the (demo-free) tenant list.
      return new Set(this.tenants.filter(t => t.bus_sos_enabled && t.bus_sos_receive_from_other_companies && !t.demo).map(t => t.tenant_id))
    },
    tenantMeta() {
      // facet keys per tenant, so a yard can be filtered like the vehicles of its tenant
      const meta = {}
      this.tenants.forEach(t => {
        meta[t.tenant_id] = { tenant_id: t.tenant_id, type_key: this.type_key(t), telematics_key: this.telematics_key(t) }
      })
      return meta
    },
    yardPoints() {
      // the yards view: one marker per yard whose tenant passes the tenant-level filters (and
      // the range pin), carrying the number of vehicles parked there that pass every filter
      const parked = {}
      for (const v of this.vehicles) {
        if (v.yard == null || !this.passes(v, 'yards')) continue
        parked[v.yard] = (parked[v.yard] || 0) + 1
      }
      const out = []
      for (const y of this.yards) {
        const meta = this.tenantMeta[y.tenant_id]
        if (!meta) continue
        const probe = { ...meta, lat: y.lat, lon: y.lon, yard: y.key }
        if (!this.passes(probe, 'yards')) continue
        out.push({ tenant_id: y.tenant_id, wx: y.wx, wy: y.wy, lat: y.lat, lon: y.lon, n: parked[y.key] || 0, color: this.colorFor(probe) })
      }
      return out
    },
    sharedTenants() {
      // every account this tenant has was fetched under a sister tenant (same key + tag filter)
      return this.tenants.filter(t => t.shared_with)
    },
    erroredTenants() {
      return this.tenants.filter(t => t.error)
    },
    rangeMeters() {
      return this.range_radius * 1609.34
    },
    rangePinIcon() {
      // distinct red dot for the range pin (data markers are colored squares)
      return {
        path: 'M 0,-7 A 7,7 0 1,1 0,7 A 7,7 0 1,1 0,-7 z',
        fillColor: '#c0392b',
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 2,
        scale: 1.1,
      }
    },
    points() {
      // what the canvas draws: the vehicles passing every facet (and the range pin), as
      // precomputed world coords + color. the bus name comes along for the Bus SOS testing
      // view (labels), and is null for every non-SOS tenant.
      const out = []
      for (const v of this.vehicles) {
        if (!this.passes(v)) continue
        out.push({ tenant_id: v.tenant_id, wx: v.wx, wy: v.wy, lat: v.lat, lon: v.lon, yard: v.yard, name: v.name, color: this.colorFor(v) })
      }
      // grouped by color so the canvas changes fillStyle a handful of times, not per square
      out.sort((a, b) => (a.color < b.color ? -1 : a.color > b.color ? 1 : 0))
      return out
    },
    onMapList() {
      // what the On map list spells out. a drawn vehicle whose id reached the page gets its own
      // row (the api sends vehicle names for Bus SOS tenants only -- see get_fleet_map_for_api),
      // and everything else is rolled up into one row per tenant carrying its count, so the list
      // never shows a blank where an id was withheld. yards mode rolls up the same way.
      const rows = []
      const rolled = new Map()
      const yardsMode = this.yards_only
      const source = yardsMode ? this.yardPoints : this.points
      for (const p of source) {
        const owner = this.tenantNames[p.tenant_id] || p.tenant_id
        if (!yardsMode && p.name) {
          rows.push({ key: p.tenant_id + '|' + p.name + '|' + rows.length, group: owner, label: p.name, tenant: owner, color: p.color })
          continue
        }
        let row = rolled.get(p.tenant_id)
        if (!row) {
          row = { key: 'tenant:' + p.tenant_id, group: owner, label: owner, color: p.color, count: 0 }
          rolled.set(p.tenant_id, row)
          rows.push(row)
        }
        row.count++
      }
      // by company, then by vehicle number the way a person reads it (2 before 10)
      rows.sort((a, b) => a.group.localeCompare(b.group) || a.label.localeCompare(b.label, undefined, { numeric: true }))
      return {
        rows: rows.length > MAX_ON_MAP_ROWS ? rows.slice(0, MAX_ON_MAP_ROWS) : rows,
        more: Math.max(0, rows.length - MAX_ON_MAP_ROWS),
      }
    },
  },
  mounted() {
    this._map = null
    this.load_vehicles()
    if (this.$refs.overlayMap) {
      this.$refs.overlayMap.$mapPromise.then(map => { this._map = map; this.install_overlay(map) })
    }
  },
  beforeDestroy() {
    if (this._overlay) {
      this._overlay.setMap(null)
      this._overlay = null
    }
  },
  methods: {
    type_key(v) {
      return (v.tenant_type || UNKNOWN_KEY).toString().toUpperCase()
    },
    telematics_key(v) {
      return (v.telematics_type || UNKNOWN_KEY).toString().toUpperCase()
    },
    is_on(map, key) {
      return (key in map) ? map[key] : true
    },
    passes(v, except) {
      // does this vehicle pass every filter, optionally ignoring one ('sos', 'type',
      // 'telematics' or 'tenant') so that facet's own list can be computed from the rest
      if (except !== 'sos' && this.sos_only && !this.sosTenantIds.has(v.tenant_id)) return false
      if (except !== 'yards' && this.yards_only && v.yard == null) return false
      if (except !== 'type' && !this.is_on(this.type_on, v.type_key)) return false
      if (except !== 'telematics' && !this.is_on(this.telematics_on, v.telematics_key)) return false
      if (except !== 'tenant' && !this.is_on(this.tenant_on, v.tenant_id)) return false
      if (this.range_pin && this.distanceMiles(this.range_pin, v) > this.range_radius) return false
      return true
    },
    facet_rows(rows, keyOf, onMap) {
      const counts = {}
      rows.forEach(v => {
        const key = keyOf(v)
        counts[key] = (counts[key] || 0) + 1
      })
      return Object.keys(counts).sort().map(key => ({
        key,
        label: key,
        count: counts[key],
        on: this.is_on(onMap, key),
      }))
    },
    facet_state(kind) {
      if (kind === 'type') return { list: this.tenantTypeList, prop: 'type_on' }
      if (kind === 'telematics') return { list: this.telematicsList, prop: 'telematics_on' }
      return { list: this.tenantList, prop: 'tenant_on' }
    },
    toggle_row(kind, key, currentOn) {
      const { prop } = this.facet_state(kind)
      this.$set(this[prop], key, !currentOn)
    },
    isolate(kind, key) {
      // clicking a row shows only that row within its facet and resets the other facets to
      // all-on, so the isolated row is exactly what ends up on the map. clicking a checkbox
      // just toggles that one row (see @click.stop on the inputs)
      const facets = ['type', 'telematics', 'tenant']
      facets.forEach(facet => {
        const { list, prop } = this.facet_state(facet)
        const onMap = {}
        list.forEach(row => { onMap[row.key] = facet !== kind || row.key === key })
        this[prop] = onMap
      })
    },
    toggle_section(kind) {
      // clicking a section heading turns all of its rows on, or all off if any are on
      const { list } = this.facet_state(kind)
      this.set_section(kind, !list.some(row => row.on))
    },
    set_section(kind, on) {
      const { list, prop } = this.facet_state(kind)
      const onMap = {}
      list.forEach(row => { onMap[row.key] = on })
      this[prop] = onMap
    },
    telematicsColor(key) {
      return this.orderedColors[(key || UNKNOWN_KEY).toString().toUpperCase()] || SPARE_COLORS[0]
    },
    typeColor(key) {
      return this.typeColors[(key || UNKNOWN_KEY).toString().toUpperCase()] || SPARE_COLORS[0]
    },
    colorFor(v) {
      // the square color for a vehicle row under the active Color by mode
      if (this.color_by === 'tenant') return this.tenantColors[v.tenant_id] || SPARE_COLORS[0]
      if (this.color_by === 'type') return this.typeColors[v.type_key] || SPARE_COLORS[0]
      return this.orderedColors[v.telematics_key] || SPARE_COLORS[0]
    },
    place_range_pin(ev) {
      if (!ev || !ev.latLng) return
      this.range_pin = { lat: ev.latLng.lat(), lng: ev.latLng.lng() }
    },
    clear_range_pin() {
      this.range_pin = null
    },
    distanceMiles(pin, v) {
      // haversine distance in miles from the range pin {lat,lng} to a vehicle row {lat,lon}
      const R = 3958.8
      const toRad = d => (d * Math.PI) / 180
      const dLat = toRad(v.lat - pin.lat)
      const dLng = toRad(v.lon - pin.lng)
      const lat1 = toRad(pin.lat)
      const lat2 = toRad(v.lat)
      const h = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) * Math.sin(dLng / 2)
      return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)))
    },
    as_of(iso) {
      // format a telematics ISO timestamp into the viewer's local time
      if (!iso) return ''
      const d = new Date(iso)
      if (isNaN(d.getTime())) return iso
      return 'As of ' + d.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
    },
    install_overlay(map) {
      // one canvas in the (non-interactive) overlay pane, redrawn on map moves and filter
      // changes. thousands of google markers each cost a DOM node and an event target; a
      // fillRect per vehicle costs microseconds.
      const google = window.google
      if (!google || !google.maps) return
      const vm = this
      const overlay = new google.maps.OverlayView()
      overlay.onAdd = function () {
        const canvas = document.createElement('canvas')
        canvas.style.position = 'absolute'
        canvas.style.pointerEvents = 'none'
        this.canvas = canvas
        this.getPanes().overlayLayer.appendChild(canvas)
      }
      overlay.onRemove = function () {
        if (this.canvas && this.canvas.parentNode) this.canvas.parentNode.removeChild(this.canvas)
        this.canvas = null
      }
      overlay.draw = function () {
        const proj = this.getProjection()
        const bounds = map.getBounds()
        if (!proj || !bounds || !this.canvas) return
        const ne = bounds.getNorthEast()
        const sw = bounds.getSouthWest()
        const nwDiv = proj.fromLatLngToDivPixel(new google.maps.LatLng(ne.lat(), sw.lng()))
        const seDiv = proj.fromLatLngToDivPixel(new google.maps.LatLng(sw.lat(), ne.lng()))
        const width = Math.max(1, Math.round(seDiv.x - nwDiv.x))
        const height = Math.max(1, Math.round(seDiv.y - nwDiv.y))
        const dpr = window.devicePixelRatio || 1
        const canvas = this.canvas
        canvas.style.left = nwDiv.x + 'px'
        canvas.style.top = nwDiv.y + 'px'
        canvas.style.width = width + 'px'
        canvas.style.height = height + 'px'
        canvas.width = Math.round(width * dpr)
        canvas.height = Math.round(height * dpr)
        const ctx = canvas.getContext('2d')
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
        ctx.clearRect(0, 0, width, height)
        // world -> canvas px: scale by 2^zoom, offset by the canvas's own world origin
        const scale = Math.pow(2, map.getZoom())
        const nw = worldPoint(ne.lat(), sw.lng())
        const SQUARE = vm.marker_size
        const half = SQUARE / 2

        const drawBubbles = (bubbles, ring) => {
          // biggest last so a large one never hides under a small one
          bubbles.sort((a, b) => a.n - b.n)
          ctx.textAlign = 'center'
          ctx.textBaseline = 'middle'
          for (const b of bubbles) {
            const r = b.n ? Math.min(9 + 3.2 * Math.log2(b.n), 24) : 7
            ctx.beginPath()
            ctx.arc(b.x, b.y, r, 0, Math.PI * 2)
            ctx.fillStyle = b.color
            ctx.globalAlpha = b.n ? 0.88 : 0.5
            ctx.fill()
            ctx.globalAlpha = 1
            ctx.lineWidth = ring ? 2 : 1.5
            ctx.strokeStyle = ring ? '#1c2733' : 'rgba(255,255,255,0.9)'
            ctx.stroke()
            if (b.n) {
              ctx.fillStyle = '#fff'
              ctx.font = 'bold ' + (b.n >= 1000 ? 10 : 11) + 'px system-ui, -apple-system, Segoe UI, sans-serif'
              ctx.fillText(formatCount(b.n), b.x, b.y + 0.5)
            }
          }
        }

        if (vm.yards_only) {
          // the yards view: a dark-ringed marker per yard with its parked count (an empty
          // yard is a small faded dot); no vehicle squares at all
          const marks = []
          for (const y of vm.yardPoints) {
            const x = (y.wx - nw.wx) * scale
            const yy = (y.wy - nw.wy) * scale
            if (x < -30 || yy < -30 || x > width + 30 || yy > height + 30) continue
            marks.push({ x, y: yy, n: y.n, color: y.color })
          }
          drawBubbles(marks, true)
          return
        }

        const mode = vm.cluster_mode
        const expandYards = mode === 'yards' && map.getZoom() >= YARD_EXPAND_ZOOM
        if (mode === 'off' || expandYards) {
          let color = null
          // the Bus SOS testing view labels each bus with its name, drawn after the squares
          // so no square paints over another bus's label. a white halo keeps them legible on
          // any basemap. the sos_only filter already narrows to a handful of tenants, so the
          // labels show at any zoom (the national view included).
          const labels = vm.sos_only ? [] : null
          for (const p of vm.points) {
            if (p.color !== color) {
              color = p.color
              ctx.fillStyle = color
            }
            const x = (p.wx - nw.wx) * scale
            const y = (p.wy - nw.wy) * scale
            if (x < -half || y < -half || x > width + half || y > height + half) continue
            ctx.fillRect(x - half, y - half, SQUARE, SQUARE)
            if (labels && p.name) labels.push({ x, y, name: p.name })
          }
          if (labels && labels.length) {
            ctx.font = 'bold 11px system-ui, -apple-system, Segoe UI, sans-serif'
            ctx.textAlign = 'left'
            ctx.textBaseline = 'middle'
            ctx.lineWidth = 3
            ctx.strokeStyle = 'rgba(255,255,255,0.9)'
            ctx.fillStyle = '#1c2733'
            for (const l of labels) {
              const tx = l.x + half + 3
              ctx.strokeText(l.name, tx, l.y)
              ctx.fillText(l.name, tx, l.y)
            }
          }
          return
        }

        // group on-screen squares: in dense mode by CELL-sized screen grid cell, in yards mode
        // by the tenant yard the vehicle is parked at (road vehicles get a cell of their own).
        // a group past the threshold becomes one bubble carrying its count in the color most
        // of its members use
        const minSize = mode === 'yards' ? YARD_MIN : CLUSTER_MIN
        const cells = new Map()
        let loose = 0
        for (const p of vm.points) {
          const x = (p.wx - nw.wx) * scale
          const y = (p.wy - nw.wy) * scale
          if (x < -half || y < -half || x > width + half || y > height + half) continue
          let key
          if (mode === 'yards') {
            key = p.yard != null ? p.yard : 'loose:' + (loose++)
          } else {
            key = ((x / CELL) | 0) * 100000 + ((y / CELL) | 0)
          }
          let cell = cells.get(key)
          if (!cell) {
            cell = { n: 0, sx: 0, sy: 0, xs: [], ys: [], colors: [], tally: {} }
            cells.set(key, cell)
          }
          cell.n++
          cell.sx += x
          cell.sy += y
          cell.xs.push(x)
          cell.ys.push(y)
          cell.colors.push(p.color)
          cell.tally[p.color] = (cell.tally[p.color] || 0) + 1
        }

        const bubbles = []
        for (const cell of cells.values()) {
          if (cell.n < minSize) {
            for (let i = 0; i < cell.n; i++) {
              ctx.fillStyle = cell.colors[i]
              ctx.fillRect(cell.xs[i] - half, cell.ys[i] - half, SQUARE, SQUARE)
            }
            continue
          }
          let best = null
          for (const c in cell.tally) if (!best || cell.tally[c] > cell.tally[best]) best = c
          bubbles.push({ x: cell.sx / cell.n, y: cell.sy / cell.n, n: cell.n, color: best })
        }
        // bubbles on top of squares
        drawBubbles(bubbles, false)
      }
      overlay.setMap(map)
      this._overlay = overlay
      // the overlay only repaints on map moves, and the fixed initial CONUS view triggers none:
      // force a first paint as soon as the projection is ready, so a snapshot that finished loading
      // before the map was ready still appears instead of staying blank until the first filter edit.
      const ensureFirstDraw = (tries) => {
        if (this._overlay && this._overlay.getProjection && this._overlay.getProjection()) this._overlay.draw()
        else if (tries > 0) window.requestAnimationFrame(() => ensureFirstDraw(tries - 1))
      }
      ensureFirstDraw(60)
    },
    redraw() {
      if (this._overlay && this._overlay.getProjection && this._overlay.getProjection()) this._overlay.draw()
    },
    fit_to_points() {
      // after a filter change, frame whatever is left on the map. the initial load keeps the
      // fixed CONUS view (see CONUS_CENTER); only filter edits call this. When a range pin is set,
      // also enclose the WHOLE search circle so changing the radius zooms to keep the full circle
      // on screen instead of letting it spill past the map edge.
      const map = this._map
      const g = window.google && window.google.maps
      const pts = this.yards_only ? this.yardPoints : this.points
      const hasPin = !!this.range_pin
      if (!map || !g || (!pts.length && !hasPin)) return
      const bounds = new g.LatLngBounds()
      for (const p of pts) bounds.extend({ lat: p.lat, lng: p.lon })
      if (hasPin && this.rangeMeters) {
        const c = this.range_pin
        const latOff = this.rangeMeters / 111320
        const lngOff = this.rangeMeters / (111320 * Math.cos(c.lat * Math.PI / 180))
        bounds.extend({ lat: c.lat + latOff, lng: c.lng + lngOff })
        bounds.extend({ lat: c.lat - latOff, lng: c.lng - lngOff })
      }
      map.fitBounds(bounds, 40)
      // a single tenant in one yard would otherwise zoom to street level
      g.event.addListenerOnce(map, 'idle', () => {
        if (map.getZoom() > 12) map.setZoom(12)
      })
    },
    refit() {
      this.$nextTick(this.fit_to_points)
    },
    async load_vehicles() {
      this.loadError = false
      this.vehicles_loading = true
      try {
        const { value: { data = {} } } = await get_admin_fleet_map()
        // precompute everything draw and the facets need, then freeze: vue never has to
        // observe these rows, which is the single biggest win at this row count
        this.vehicles = Object.freeze((data.vehicles || []).map(v => {
          const lat = Number(v.lat)
          const lon = Number(v.lon)
          return Object.freeze({
            tenant_id: v.tenant_id,
            type_key: this.type_key(v),
            telematics_key: this.telematics_key(v),
            // bus name rides along only for bus_sos_enabled tenants (api withholds it otherwise);
            // drawn as a label on the map in the Bus SOS testing view
            name: v.name || null,
            lat,
            lon,
            // tenant-scoped yard key when parked at one of the tenant's yards (api tags the index)
            yard: v.yard == null ? null : v.tenant_id + '|' + v.yard,
            ...worldPoint(lat, lon),
          })
        }).filter(v => !isNaN(v.lat) && !isNaN(v.lon)))
        this.tenants = Object.freeze((data.tenants || []).map(t => Object.freeze(t)))
        this.yards = Object.freeze((data.yards || []).map(y => {
          const lat = Number(y.lat)
          const lon = Number(y.lon)
          return Object.freeze({ tenant_id: y.tenant_id, key: y.tenant_id + '|' + y.yard, lat, lon, ...worldPoint(lat, lon) })
        }).filter(y => !isNaN(y.lat) && !isNaN(y.lon)))
        this.generated_at = data.generated_at || null
        this.source = data.source || null
      } catch (e) {
        this.loadError = true
        this.vehicles = []
        this.tenants = []
      } finally {
        this.vehicles_loading = false
      }
    },
  },
  watch: {
    points() {
      this.redraw()
    },
    yardPoints() {
      this.redraw()
    },
    cluster_mode() {
      this.redraw()
    },
    marker_size() {
      this.redraw()
    },
    // every filter input refits the map; loading and color changes do not
    type_on: { deep: true, handler() { this.refit() } },
    telematics_on: { deep: true, handler() { this.refit() } },
    tenant_on: { deep: true, handler() { this.refit() } },
    sos_only(on) {
      // the Bus SOS testing view is where same-provider companies must be told apart, so turning
      // it on switches to per-tenant colors and draws every bus as a labelled square (clustering
      // would hide the labels). the user can still change Color by / Cluster afterwards.
      if (on) {
        if (this.color_by !== 'tenant') this.color_by = 'tenant'
        if (this.cluster_mode !== 'off') this.cluster_mode = 'off'
      }
      this.refit()
    },
    yards_only() { this.refit() },
    range_pin() { this.refit() },
    range_radius() { this.refit() },
  },
}
</script>

<style scoped>
.overlay-wrap {
  display: flex;
  gap: 16px;
  padding: 16px;
  align-items: flex-start;
}
.overlay-panel {
  flex: 0 0 288px;
  min-width: 0;   /* defeat the flex min-content floor so long list rows ellipsize, not widen the column */
  box-sizing: border-box;
  background: #fff;
  border: 1px solid #e6e9ec;
  border-radius: 10px;
  padding: 14px;
}
.overlay-panel-title {
  font-weight: 700;
  font-size: 16px;
  color: #1c2733;
  margin-bottom: 6px;
  text-align: left;
}
.overlay-group {
  margin-bottom: 6px;
}
.overlay-group-title {
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #8295a7;
  text-align: left;
  margin: 6px 0 2px;
}
.overlay-group-title--btn {
  display: block;
  width: 100%;
  background: none;
  border: none;
  padding: 2px 0;
  font-family: inherit;
  font-size: 12px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  font-weight: 700;
  cursor: pointer;
}
.overlay-group-title--btn:hover {
  color: #47566a;
}
.overlay-group-title--inline {
  margin: 0;
}
.overlay-group-head {
  display: flex;
  align-items: center;
  gap: 4px;
}
.overlay-group-head .overlay-group-title--btn {
  flex: 1 1 auto;
  width: auto;
}
.overlay-mini-btn {
  flex: 0 0 auto;
  border: 1px solid #cdd6de;
  background: #fff;
  color: #47566a;
  border-radius: 4px;
  font: inherit;
  font-size: 11px;
  line-height: 1;
  padding: 3px 6px;
  cursor: pointer;
}
.overlay-mini-btn:hover:not(:disabled) {
  border-color: #8295a7;
}
.overlay-mini-btn:disabled {
  opacity: 0.45;
  cursor: default;
}
.overlay-layer {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
  color: #1c2733;
  text-align: left;
  margin: 0;
  padding: 1px 0;
  cursor: pointer;
}
.overlay-swatch {
  width: 11px;
  height: 11px;
  border-radius: 3px;
  flex: 0 0 auto;
}
.overlay-layer-label {
  flex: 0 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.overlay-network-list {
  max-height: 320px;
  overflow-y: auto;
}
.overlay-onmap-list {
  max-height: 220px;
  overflow-y: auto;
  margin-top: 2px;
}
.overlay-onmap-tenant {
  flex: 0 1 auto;
  min-width: 0;
  margin-left: auto;
  padding-left: 8px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #6e7e8f;
  font-size: 12px;
  font-weight: 400;
}
.overlay-member-tag {
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #47566a;
  background: #eef1f3;
  border-radius: 8px;
  padding: 1px 6px;
  flex: 0 0 auto;
}
.overlay-member-tag--sos {
  color: #ae3f2e;
  background: #f6e4e0;
}
.overlay-count {
  flex: 0 0 auto;
  margin-left: auto;
  font-variant-numeric: tabular-nums;
  color: #6e7e8f;
  font-size: 12px;
}
.overlay-hint {
  font-size: 12px;
  color: #8295a7;
  margin-top: 8px;
}
.overlay-hint--tight {
  margin-top: 2px;
  margin-left: 20px;
}
.overlay-hint--nomargin {
  margin-left: 0;
}
.overlay-hint--error {
  color: #ae3f2e;
  cursor: help;
}
.overlay-shown {
  border-top: 1px solid #eef1f3;
  padding-top: 10px;
  margin-top: 6px;
}
.overlay-shown .overlay-group-title {
  display: flex;
  align-items: center;
}
.overlay-colorby {
  display: flex;
  gap: 4px;
}
.overlay-combo-legend {
  margin-top: 4px;
}
.overlay-layer--static {
  cursor: default;
}
.overlay-colorby-btn {
  flex: 1 1 0;
  border: 1px solid #cdd6de;
  background: #fff;
  color: #47566a;
  border-radius: 6px;
  font: inherit;
  font-size: 12px;
  padding: 3px 4px;
  cursor: pointer;
}
.overlay-colorby-btn:hover {
  border-color: #8295a7;
}
.overlay-colorby-btn--on {
  background: #1c2733;
  border-color: #1c2733;
  color: #fff;
}
/* range filter control */
.overlay-range-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.overlay-range-label {
  font-size: 13px;
  color: #47566a;
}
.overlay-range-clear {
  border: none;
  background: none;
  color: #c0392b;
  font-size: 15px;
  line-height: 1;
  padding: 2px 4px;
  cursor: pointer;
}
.overlay-range-clear:hover {
  color: #922b21;
}
.overlay-range-slider {
  width: 100%;
  margin-top: 2px;
}
.overlay-mapwrap {
  flex: 1 1 auto;
  min-width: 0;
}
.overlay-map {
  width: 100%;
  height: 70vh;
  min-height: 380px;
  border-radius: 10px;
  overflow: hidden;
}
.overlay-state--error {
  color: #ae3f2e;
  background: #f6e4e0;
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 10px;
}

@media (max-width: 768px) {
  .overlay-wrap {
    flex-direction: column;
  }
  .overlay-panel {
    flex: 1 1 auto;
    width: 100%;
    box-sizing: border-box;
  }
  .overlay-map {
    height: 60vh;
  }
}
</style>
